import { useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  detectDocumentApi,
  detectDocumentMultiApi,
  createPendingRecordApi,
  updatePendingRecordApi,
  listPendingRecordsApi,
  deletePendingRecordApi,
  clearAllPendingRecordsApi,
  enhancePortraitApi,
  PendingRecordDto,
  type DocTypeHint,
} from '../services/trungTamApi';
import type { DocDetectResult, DocType, ExtraImage } from '../types';
import { decodeCccdQrFromFile, parseCccdQrPipe } from '../services/zxingQr';
import { concatImagesVertically } from '../services/concatImages';

/**
 * Surface backend AI-related warnings in a deduped toast. EC=2 with
 * DT.aiKind covers three operator-actionable cases:
 *   - not_configured / no_key → orange warn, "liên hệ SupperAdmin"
 *   - type_mismatch           → red error, "ảnh không đúng loại đã chọn"
 * Toast id is keyed on `aiKind` so a 5-image batch only renders 1 toast
 * per kind even if every image triggers the same condition.
 */
const warnAiNotReady = (em: string, kind: string | undefined) => {
  const isUserError = kind === 'type_mismatch';
  const fn = isUserError ? toast.error : toast.warn;
  fn(em, {
    toastId: `ai-${kind || 'unknown'}`,
    autoClose: isUserError ? 7000 : 6000,
  });
};

/** One staged image carrying an optional doc-type hint. When the hint is
 *  present, detectDocumentApi sends it to the backend which skips the
 *  expensive auto-classify prompt (3-5x faster) — or short-circuits the
 *  Mistral call entirely for 'portrait' / 'other'. */
export type StagingItem = { file: File; hint: DocTypeHint };

const isStagingItem = (x: File | StagingItem): x is StagingItem =>
  !!(x as StagingItem).file && !!(x as StagingItem).hint;

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });

/** Normalise date strings to strict DD/MM/YYYY. The CCCD QR packs dates
 *  as 8 raw digits ("DDMMYYYY") with no separator — the form expects
 *  slashes, so reformat before the value reaches component state. */
const formatClientVnDate = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const digits = s.replace(/\D/g, '');
  if (digits.length === 8) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  }
  const m = s.match(/^(\d{1,2})[^\d]+(\d{1,2})[^\d]+(\d{2,4})$/);
  if (m) {
    const dd = m[1].padStart(2, '0');
    const mm = m[2].padStart(2, '0');
    let yyyy = m[3];
    if (yyyy.length === 2) yyyy = parseInt(yyyy, 10) < 50 ? `20${yyyy}` : `19${yyyy}`;
    return `${dd}/${mm}/${yyyy}`;
  }
  return s;
};

/** Shrink an image to `maxDim` on its long edge and return the result as
 *  a data-URL base64 string. Used for the copy of each upload we persist
 *  in the pending-record DB row — a raw 4000×3000 phone photo serialises
 *  to 4-8 MB of base64 which stalls the PUT on mobile networks and bloats
 *  DB writes. 1400 px at 80% JPEG is plenty for operator review (zoom UI
 *  caps at 800 px wide anyway) and trims payload ~8-10x.  Falls back to
 *  the original bytes when the file is small or resize fails. */
const fileToStoredBase64 = async (file: File, maxDim = 1400): Promise<string> => {
  if (!file.type.startsWith('image/') || file.size < 250_000) {
    return fileToBase64(file);
  }
  try {
    const dataUrl = await fileToBase64(file);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('decode'));
      i.src = dataUrl;
    });
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    if (!w || !h) return dataUrl;
    const longEdge = Math.max(w, h);
    if (longEdge <= maxDim) return dataUrl;
    const scale = maxDim / longEdge;
    const tw = Math.round(w * scale);
    const th = Math.round(h * scale);
    const canvas = document.createElement('canvas');
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0, tw, th);
    return canvas.toDataURL('image/jpeg', 0.8);
  } catch {
    return fileToBase64(file);
  }
};

const stripPrefix = (s: string | null | undefined): string | null => {
  if (!s) return null;
  return s.includes(',') ? s.split(',', 2)[1] : s;
};

/** Canonical old→new mapping per Luật TTATGT 2024 — kept in sync with
 *  HANG_GPLX_MAP in TtScannerForm.tsx. Quick subset only (common ops
 *  path). Anything not in this list passes through unchanged; the detail
 *  UI uses the full table. */
const convertHangGPLX = (hang: string): string => {
  const h = (hang || '').toUpperCase().trim();
  if (h === 'B1' || h === 'B2') return 'B';
  if (h === 'C') return 'Cm';
  if (h === 'D') return 'D2';
  if (h === 'E') return 'Dm';
  if (h === 'A2') return 'Am';
  if (h === 'A1') return 'A.03';
  if (h === 'FB2') return 'BE';
  if (h === 'FC') return 'CE';
  if (h === 'FD') return 'D2E';
  if (h === 'FE') return 'DE';
  return h;
};

const toDocType = (dt: string): DocType => {
  if (dt === 'cccd') return 'cccd';
  if (dt === 'gplx') return 'gplx';
  if (dt === 'health') return 'health';
  return 'other';
};

type MergedPayload = {
  soCCCD?: string;
  soCMT?: string;
  hoTen?: string;
  ngaySinh?: string;
  gioiTinh?: string;
  diaChi?: string;
  ngayCap?: string;
  soGPLX?: string;
  hangGPLX?: string;
  hangGPLXMoi?: string;
  ngayCapGPLX?: string;
  ngayTrungTuyenGPLX?: string;
  ngayHetHanGPLX?: string;
  avatarBase64?: string;
  sourceImageBase64?: string;
  portraitBase64?: string;
  // Raw selfie before AI enhance — persisted via pending record so
  // final save can carry it into tt_student_record.
  portraitOriginalBase64?: string;
  extraImagesJson?: string;
  // Track detect source separately for each document so the review UI
  // can render a mixed QR+OCR badge when e.g. CCCD was QR-decoded but
  // the GPLX had to fall back to Mistral OCR.
  cccdSource?: 'qr' | 'ocr' | null;
  gplxSource?: 'qr' | 'ocr' | null;
};

const applyDetection = (
  prev: MergedPayload,
  det: DocDetectResult,
  imageBase64: string,
  extras: ExtraImage[],
): { next: MergedPayload; extras: ExtraImage[] } => {
  const sideLabel = det.docSide === 'back' ? `${toDocType(det.docType)}_back`
                  : det.docSide === 'front' ? `${toDocType(det.docType)}_front`
                  : undefined;

  let newExtras = [...extras, { base64: imageBase64, docType: toDocType(det.docType), label: sideLabel }];
  const next: MergedPayload = { ...prev };

  const src: 'qr' | 'ocr' = det.detectSource === 'qr' ? 'qr' : 'ocr';

  if (det.docType === 'cccd' && det.cccd) {
    const c = det.cccd;
    if (c.soCCCD && !next.soCCCD) next.soCCCD = c.soCCCD;
    if (c.soCMT && !next.soCMT) next.soCMT = c.soCMT;
    if (c.hoTen && !next.hoTen) next.hoTen = c.hoTen.toUpperCase();
    if (c.ngaySinh && !next.ngaySinh) next.ngaySinh = c.ngaySinh;
    if (c.gioiTinh && !next.gioiTinh) next.gioiTinh = c.gioiTinh;
    // diaChi = NƠI THƯỜNG TRÚ only. Old CCCD: noiThuongTru. New Căn cước:
    // noiCuTru. Never fall back to queQuan / noiDangKyKhaiSinh or anything
    // from GPLX — those are not permanent residence.
    if ((c.noiThuongTru || c.noiCuTru) && !next.diaChi) {
      next.diaChi = c.noiThuongTru || c.noiCuTru;
    }
    if (c.ngayCap) {
      if (!next.ngayCap || det.docSide === 'back') next.ngayCap = c.ngayCap;
    }
    // QR beats a prior OCR tag — authoritative source wins.
    if (!next.cccdSource || (next.cccdSource === 'ocr' && src === 'qr')) {
      next.cccdSource = src;
    }
  } else if (det.docType === 'gplx' && det.gplx) {
    const g = det.gplx;
    if (g.soGPLX) next.soGPLX = g.soGPLX;
    if (g.hang) {
      next.hangGPLX = g.hang;
      next.hangGPLXMoi = convertHangGPLX(g.hang);
    }
    if (g.ngayCap) next.ngayCapGPLX = g.ngayCap;
    if (g.ngayTrungTuyen) next.ngayTrungTuyenGPLX = g.ngayTrungTuyen;
    if (g.ngayHetHan) next.ngayHetHanGPLX = g.ngayHetHan;
    if (g.hoTen && !next.hoTen) next.hoTen = g.hoTen.toUpperCase();
    if (g.ngaySinh && !next.ngaySinh) next.ngaySinh = g.ngaySinh;
    if (!next.gplxSource || (next.gplxSource === 'ocr' && src === 'qr')) {
      next.gplxSource = src;
    }
  } else if (det.docType === 'portrait' || det.portrait) {
    const clean = stripPrefix(imageBase64);
    if (clean) next.portraitBase64 = clean;
    newExtras = newExtras.filter(e => e.base64 !== imageBase64);
  }

  return { next, extras: newExtras };
};

export const useBatchQueue = () => {
  const [queueRecords, setQueueRecords] = useState<PendingRecordDto[]>([]);
  const [queueProcessing, setQueueProcessing] = useState(false);
  // filesMap stores the raw staging items (file + optional hint) keyed by
  // pending record id. Hint is preserved so processRecord can pass it to
  // the detect API and bypass classification on the backend.
  const filesMap = useRef<Map<number, StagingItem[]>>(new Map());

  // Load persisted pending records on mount so the queue survives reloads.
  useEffect(() => {
    (async () => {
      try {
        const resp = await listPendingRecordsApi();
        if (resp.EC === 0 && Array.isArray(resp.DT)) {
          setQueueRecords(resp.DT);
        }
      } catch { /* silent — queue stays empty */ }
    })();
  }, []);

  const processRecord = useCallback(async (recordId: number, items: StagingItem[]) => {
    // Full detection lifecycle logs — prefixed with the record id so
    // multiple in-flight records can be distinguished in the console.
    // Each step logs elapsed ms since the record started.
    const tStart = performance.now();
    const log = (step: string, extra: string = '') => {
      const ms = Math.round(performance.now() - tStart);
      // eslint-disable-next-line no-console
      console.log(`[QUEUE:${recordId}] t+${ms}ms ${step}${extra ? ' ' + extra : ''}`);
    };
    log('start', `items=${items.length} hints=[${items.map(i => i.hint).join(',')}]`);
    try {
      await updatePendingRecordApi(recordId, { status: 'detecting', currentStep: 'Đang nhận diện...' });
      log('status→detecting');
      setQueueRecords(prev => prev.map(r => r.id === recordId
        ? { ...r, status: 'detecting', currentStep: 'Đang nhận diện...' } : r));

      // Face crop removed per operator request — it added 3-10s of
      // latency (Python YuNet warm-up + upload over tunnel) for an
      // avatar that wasn't critical. The CCCD source image still
      // shows in the review UI; any crop the operator needs they can
      // do manually.

      // Serialize detect calls. Backend mistralPost has MAX_CONCURRENCY=1 so
      // firing N POSTs in parallel just queues them server-side AND races
      // each request against the axios timeout — the 3rd image times out
      // before Mistral can even start on it. Running one at a time lets
      // each request use the full timeout budget and surfaces partial
      // progress to the UI (we update currentStep between files).
      // Each item carries its own docType hint; backend short-circuits for
      // 'portrait' / 'other' (no Mistral) and uses a narrow extraction
      // prompt for 'cccd' / 'gplx' (~3-5x faster than auto-classify).
      const stepLabel = (hint: DocTypeHint): string => {
        if (hint === 'cccd') return 'Đọc CCCD';
        if (hint === 'gplx') return 'Đọc GPLX';
        if (hint === 'mixed') return 'Đọc CCCD + GPLX (gộp)';
        if (hint === 'portrait') return 'Lưu ảnh thẻ';
        return 'Lưu giấy tờ khác';
      };
      // COST OPTIMISATION — concat same-type pages into one OCR call.
      // mistral-ocr-latest bills per page. If the operator staged 2
      // CCCD images (front+back) we stack them vertically into a
      // single JPEG and send ONE detect request — same data extracted,
      // half the tokens, half the mobile uploads. Portrait / other
      // stay 1:1. We try ZXing QR on each original image BEFORE
      // concat though — QR payload is authoritative and cheap.
      const tryClientQr = async (file: File, hint: DocTypeHint): Promise<DocDetectResult | null> => {
        if (hint !== 'cccd') return null;
        try {
          const qrText = await decodeCccdQrFromFile(file);
          const parsed = qrText ? parseCccdQrPipe(qrText) : null;
          if (!parsed) return null;
          return {
            docType: 'cccd',
            docSide: 'unknown',
            savedUrl: undefined,
            detectSource: 'qr',
            cccd: {
              soCCCD: parsed.soCCCD,
              soCMT: parsed.soCMT,
              hoTen: parsed.hoTen,
              ngaySinh: formatClientVnDate(parsed.ngaySinh),
              gioiTinh: parsed.gioiTinh,
              queQuan: '',
              diaChi: parsed.diaChi,
              noiCuTru: '',
              noiThuongTru: parsed.diaChi,
              ngayCap: formatClientVnDate(parsed.ngayCap),
            },
          } as DocDetectResult;
        } catch {
          return null;
        }
      };

      // Probe each CCCD/GPLX item with client-side ZXing first — any
      // QR hit bypasses both concat AND the Mistral OCR call.
      const probed = await Promise.all(items.map(async (it) => ({
        ...it,
        clientQr: await tryClientQr(it.file, it.hint),
      })));

      // Group-level QR coverage — if ANY image in a hint group has a QR
      // hit, the QR payload already carries every field the card exposes
      // (CCCD QR = soCCCD + hoTen + ngaySinh + gioiTinh + diaChi + ngayCap;
      // GPLX QR = soGPLX + hoTen + hang + ngaySinh + …). The "other side"
      // of the same card has no unique information we'd gain from OCR, so
      // any non-QR sibling in the same group is stored as a companion
      // image and NEVER sent to Mistral. Saves 1 OCR call per group every
      // time the operator scans both sides of a QR-bearing card.
      const hintHasQr: Record<string, boolean> = {
        cccd: probed.some(p => p.hint === 'cccd' && !!p.clientQr),
        gplx: probed.some(p => p.hint === 'gplx' && !!p.clientQr),
      };
      const passthrough: typeof probed = [...probed];

      // Serialize per-image detection. Firing all N uploads in parallel
      // over mobile 5G saturates the cloudflared tunnel uplink and the
      // iPhone's per-host HTTP/2 stream limit (6 concurrent = 5 detect +
      // 1 other call in flight), producing "Lỗi mạng" toasts while the
      // earliest requests still succeed. Serial uploads give each image
      // the full available bandwidth and a clean connection.
      let resolvedCount = 0;
      const detections: { file: File; b64: string; detect: DocDetectResult | null; hint: DocTypeHint }[] = [];
      // Small retry helper — a network error on one upload shouldn't
      // take down the whole staged batch. Retry once after a short
      // pause before giving up and treating the item as undetected.
      const detectWithRetry = async (file: File, hint: DocTypeHint) => {
        const label = `detect(${hint},${file.name || 'file'},${file.size}B)`;
        log(`${label} try1-start`);
        const t1 = performance.now();
        try {
          const r = await detectDocumentApi(file, hint);
          log(`${label} try1-ok`, `elapsed=${Math.round(performance.now() - t1)}ms EC=${r?.EC}`);
          return r;
        } catch (err) {
          const msg = (err as { message?: string })?.message || 'unknown';
          log(`${label} try1-FAIL`, `elapsed=${Math.round(performance.now() - t1)}ms err="${msg}"`);
          await new Promise((res) => setTimeout(res, 800));
          log(`${label} try2-start`);
          const t2 = performance.now();
          try {
            const r = await detectDocumentApi(file, hint);
            log(`${label} try2-ok`, `elapsed=${Math.round(performance.now() - t2)}ms EC=${r?.EC}`);
            return r;
          } catch (err2) {
            const m2 = (err2 as { message?: string })?.message || 'unknown';
            log(`${label} try2-FAIL`, `elapsed=${Math.round(performance.now() - t2)}ms err="${m2}"`);
            return null;
          }
        }
      };

      // Same retry pattern but for the multi-image endpoint. Used when the
      // operator stages 2 photos of the same doc type (front + back) — one
      // backend call returns a merged record so we don't burn 2 OCR calls.
      const detectMultiWithRetry = async (filesIn: File[], hint: 'cccd' | 'gplx') => {
        const label = `detect-multi(${hint},${filesIn.length} files)`;
        log(`${label} try1-start`);
        const t1 = performance.now();
        try {
          const r = await detectDocumentMultiApi(filesIn, hint);
          log(`${label} try1-ok`, `elapsed=${Math.round(performance.now() - t1)}ms EC=${r?.EC}`);
          return r;
        } catch (err) {
          const msg = (err as { message?: string })?.message || 'unknown';
          log(`${label} try1-FAIL`, `elapsed=${Math.round(performance.now() - t1)}ms err="${msg}"`);
          await new Promise((res) => setTimeout(res, 1200));
          log(`${label} try2-start`);
          const t2 = performance.now();
          try {
            const r = await detectDocumentMultiApi(filesIn, hint);
            log(`${label} try2-ok`, `elapsed=${Math.round(performance.now() - t2)}ms EC=${r?.EC}`);
            return r;
          } catch (err2) {
            const m2 = (err2 as { message?: string })?.message || 'unknown';
            log(`${label} try2-FAIL`, `elapsed=${Math.round(performance.now() - t2)}ms err="${m2}"`);
            return null;
          }
        }
      };

      // Pre-fire multi-image detect for any hint with exactly 2 OCR-eligible
      // staged items. Items with a client-side QR hit or a sibling QR don't
      // need OCR at all, so they're excluded. The merged result is owned by
      // the FIRST eligible item; the second becomes a companion photo
      // (det=null, saved as side image).
      const ocrEligible = passthrough.filter((p) =>
        (p.hint === 'cccd' || p.hint === 'gplx')
        && !p.clientQr
        && !hintHasQr[p.hint as 'cccd' | 'gplx']
      );
      type DetectApiRes = { EC: number; EM: string; DT: DocDetectResult | null } | null;
      const multiPromises: { cccd?: Promise<DetectApiRes>; gplx?: Promise<DetectApiRes> } = {};
      for (const hint of ['cccd', 'gplx'] as const) {
        const items = ocrEligible.filter((p) => p.hint === hint);
        if (items.length === 2) {
          multiPromises[hint] = detectMultiWithRetry(items.map((i) => i.file), hint);
        }
      }
      const multiAssigned: { cccd: boolean; gplx: boolean } = { cccd: false, gplx: false };
      const totalSteps = passthrough.length;

      // Run every independent task IN PARALLEL. After the concat
      // optimisation above, a typical batch generates only 2-3 network
      // requests (CCCD concat + portrait + optional GPLX), which is
      // well under iOS's 6-stream limit and won't saturate the tunnel.
      // Parallel execution cuts total wall time to the slowest single
      // request instead of the sum — operator sees ~2-3s instead of
      // ~6-8s for a typical 3-image batch.
      const tick = (hint: DocTypeHint) => {
        resolvedCount += 1;
        setQueueRecords(prev => prev.map(r => r.id === recordId
          ? { ...r, currentStep: `${stepLabel(hint)}... (${resolvedCount}/${totalSteps})` } : r));
      };

      const passthroughTasks = passthrough.map(async (p) => {
        const b64 = await fileToStoredBase64(p.file);
        let det: DocDetectResult | null = null;
        // Auto AI enhance REMOVED — operator policy: portrait upload
        // just saves the raw selfie as avatar. User clicks "✨ Tạo AI"
        // in the review popup later if they want an AI 3×4 variant.
        const enhancePromise: Promise<string | null> | null = null;

        if (p.clientQr) {
          // QR hit on THIS image — carries the whole payload for this doc.
          det = p.clientQr;
        } else if ((p.hint === 'cccd' || p.hint === 'gplx') && hintHasQr[p.hint]) {
          // Sibling in the same hint group already produced a QR hit.
          // Skip OCR — QR payload covers both sides of the card. This
          // image is still saved as a companion (docType tagged by the
          // detection-merge loop), so the review gallery shows it, but
          // no Mistral call is made for it.
          det = null;
        } else if (p.hint === 'cccd' || p.hint === 'gplx') {
          // No QR coverage for this group → OCR required.
          // If a multi-image promise was pre-fired for this hint, the FIRST
          // eligible item adopts the merged detect; siblings get null and
          // ride along as companion photos.
          const multi = multiPromises[p.hint];
          if (multi) {
            if (!multiAssigned[p.hint]) {
              multiAssigned[p.hint] = true;
              const detectResp = await multi;
              if (detectResp?.EC === 2 && detectResp.DT && (detectResp.DT as { aiKind?: string }).aiKind) {
                warnAiNotReady(detectResp.EM || 'AI chưa sẵn sàng', (detectResp.DT as { aiKind?: string }).aiKind);
              }
              det = detectResp?.EC === 0 ? detectResp.DT : null;
            } else {
              // Sibling already absorbed the merged result.
              det = null;
            }
          } else {
            const detectResp = await detectWithRetry(p.file, p.hint);
            if (detectResp?.EC === 2 && detectResp.DT && (detectResp.DT as { needsAiConfig?: boolean }).needsAiConfig) {
              warnAiNotReady(detectResp.EM || 'AI chưa sẵn sàng', (detectResp.DT as { aiKind?: string }).aiKind);
            }
            det = detectResp?.EC === 0 ? detectResp.DT : null;
          }
        } else {
          // portrait / other — never OCR'd.
          det = null;
        }
        tick(p.hint);
        return { file: p.file, b64, hint: p.hint, detect: det, enhancePromise };
      });

      // Per-image detection replaced concat (see partition comment above).
      // Kept as empty arrays so the existing Promise.all(...) call shape is
      // untouched — less churn, easier to read against git history.
      const concatTasks: Promise<typeof detections>[] = [];
      const mixedGroup: StagingItem[] = [];
      const mixedTasks = mixedGroup.length > 0
        ? [(async () => {
            const uploadFile = mixedGroup.length > 1
              ? await concatImagesVertically(mixedGroup.map((g) => g.file))
              : mixedGroup[0].file;
            const detectResp = uploadFile
              ? await detectWithRetry(uploadFile, 'mixed')
              : null;
            if (detectResp?.EC === 2 && detectResp.DT && (detectResp.DT as { needsAiConfig?: boolean }).needsAiConfig) {
              warnAiNotReady(detectResp.EM || 'AI chưa sẵn sàng', (detectResp.DT as { aiKind?: string }).aiKind);
            }
            const combined = detectResp?.EC === 0 ? detectResp.DT : null;
            const b64s = await Promise.all(mixedGroup.map((g) => fileToStoredBase64(g.file)));
            tick('mixed' as DocTypeHint);
            // Build synthetic per-image entries: the CCCD half carries the
            // cccd payload on the first CCCD-hinted image, the GPLX half
            // carries the gplx payload on the first GPLX-hinted image.
            const cccdDet: DocDetectResult | null = combined ? {
              docType: 'cccd',
              docSide: 'unknown',
              savedUrl: combined.savedUrl,
              detectSource: 'ocr',
              cccd: combined.cccd,
            } : null;
            const gplxDet: DocDetectResult | null = combined ? {
              docType: 'gplx',
              docSide: 'unknown',
              savedUrl: combined.savedUrl,
              detectSource: 'ocr',
              gplx: combined.gplx,
            } : null;
            const firstCccdIdx = mixedGroup.findIndex(g => g.hint === 'cccd');
            const firstGplxIdx = mixedGroup.findIndex(g => g.hint === 'gplx');
            return mixedGroup.map((g, gi) => ({
              file: g.file,
              b64: b64s[gi],
              hint: g.hint,
              detect: gi === firstCccdIdx ? cccdDet : gi === firstGplxIdx ? gplxDet : null,
            }));
          })()]
        : [];

      const settled = await Promise.all([
        ...passthroughTasks,
        ...concatTasks,
        ...mixedTasks,
      ]);
      for (const entry of settled) {
        if (Array.isArray(entry)) {
          for (const e of entry) detections.push(e);
        } else {
          detections.push(entry);
        }
      }

      setQueueRecords(prev => prev.map(r => r.id === recordId
        ? { ...r, currentStep: 'Đang ghép dữ liệu...' } : r));

      let merged: MergedPayload = {};
      let extras: ExtraImage[] = [];

      // Primary CCCD front → sourceImage = full card; avatar = cropped face
      // (Python YuNet/SCRFD). If no face is detected we leave avatarBase64
      // empty instead of falling back to the full card — operator prefers
      // "show if found, skip otherwise" over showing a cropped card-shaped
      // image that isn't actually a face.
      const frontCccd = detections.find(d => d.detect?.docType === 'cccd' && d.detect?.docSide === 'front');
      const anyCccd = detections.find(d => d.detect?.docType === 'cccd');
      const hintedCccd = detections.find(d => d.hint === 'cccd');
      const primary = frontCccd || anyCccd || hintedCccd;
      if (primary) {
        const cleanFull = stripPrefix(primary.b64);
        if (cleanFull) merged.sourceImageBase64 = cleanFull;
        // No face crop — avatarBase64 stays unset. The review UI falls
        // back to the full CCCD source image where an avatar would go.
      }

      // ⚡ QR-FIRST MERGE ORDER — critical bugfix:
      //   Scenario: user uploads CCCD front (no QR) + CCCD back (QR).
      //     - front processed first → OCR runs → misreads "LÂM" as "LÀM".
      //     - back processed second → QR hit has correct "LÂM".
      //     - applyDetection uses first-wins → wrong "LÀM" sticks,
      //       correct QR value is silently ignored.
      //   Fix: process QR-sourced detections BEFORE OCR-sourced, so QR
      //   always claims the merge slots. Group-level hintHasQr already
      //   skips OCR for siblings when a QR exists, but this secondary
      //   guard also covers legacy records and partial-QR cases.
      const qrFirst = [
        ...detections.filter(d => d.detect?.detectSource === 'qr'),
        ...detections.filter(d => d.detect?.detectSource !== 'qr'),
      ];
      for (const { b64, detect, hint, enhancePromise } of qrFirst as Array<{ b64: string; detect: DocDetectResult | null; hint: DocTypeHint; enhancePromise?: Promise<string | null> | null }>) {
        if (detect) {
          const beforeHoTen = merged.hoTen;
          const out = applyDetection(merged, detect, b64, extras);
          merged = out.next;
          extras = out.extras;
          // Observability — track which source filled hoTen. Helps
          // diagnose encoding / ordering regressions in the future.
          if (merged.hoTen && merged.hoTen !== beforeHoTen) {
            // eslint-disable-next-line no-console
            console.log(`[detect-merge] hoTen="${merged.hoTen}" set via ${detect.detectSource || 'unknown'} (docType=${detect.docType})`);
          }
        } else if (hint === 'portrait') {
          // Portrait hint — lưu ảnh gốc làm avatar default. AI enhance
          // KHÔNG chạy ở đây; user sẽ bấm nút "Tạo AI" trong popup chi
          // tiết hồ sơ nếu cần. `enhancePromise` đã bỏ ở trên.
          const clean = stripPrefix(b64);
          if (clean && !merged.portraitBase64) {
            merged.portraitOriginalBase64 = clean;
            merged.portraitBase64 = clean;  // default avatar = ảnh gốc
          }
          void enhancePromise;  // unused — giữ variable để khỏi TS complain
        } else if (hint === 'cccd' || hint === 'gplx') {
          // Concat group — only the FIRST image carries the shared detect
          // payload (index 0 in concatTasks). Companion images (page 2 of
          // a CCCD front+back, or page 2 of a GPLX) reach here with
          // `detect: null` but their operator-chosen hint is still 'cccd'
          // or 'gplx'. Preserve that hint as the docType so the review
          // gallery labels the image correctly — dropping to 'other' was
          // the bug where the second CCCD/GPLX page appeared as "Khác".
          extras = [...extras, { base64: b64, docType: hint as DocType, label: `${hint}_companion` }];
        } else if (hint === 'other') {
          extras = [...extras, { base64: b64, docType: 'other' as DocType, label: 'other' }];
        } else {
          extras = [...extras, { base64: b64, docType: 'other' as DocType }];
        }
      }

      // Always save the record — even when OCR extracted nothing. The
      // operator can click the row to open the images in the review
      // modal and fill any missing fields by hand. Rejecting with an
      // error + "chụp lại ảnh rõ hơn" loses the uploaded images and
      // forces a redo, which is worse UX than a partial record the
      // operator can complete manually.
      // Promote the record's scanMethod to 'qr' when ANY detection came
      // from the QR pipeline (client ZXing or server pyzbar/zxing-cpp).
      // Lets the queue list render a green "QR" badge so operators can
      // trust those records instantly vs double-checking OCR ones.
      const anyQrHit = detections.some((d) => d.detect?.detectSource === 'qr');
      const payload = {
        status: 'done' as const,
        currentStep: null,
        scanMethod: (anyQrHit ? 'qr' : 'ocr') as 'qr' | 'ocr',
        ...merged,
        extraImagesJson: JSON.stringify(extras),
      };

      // Retry the final PUT on transient network errors. Mobile tunnels
      // occasionally drop the connection during the 2-3MB JSON upload
      // (CCCD + AI portrait + original + extras) even though the server
      // successfully processed it. Without retry, the operator sees
      // "Network Error" and has to re-do the whole record even though
      // the data was saved backend-side.
      const putWithRetry = async (attempt = 1): Promise<boolean> => {
        try {
          const r = await updatePendingRecordApi(recordId, payload);
          if (r.EC === 0 && r.DT) {
            setQueueRecords(prev => prev.map(row => row.id === recordId ? r.DT! : row));
            log('done', `scanMethod=${payload.scanMethod} extras=${extras.length} attempt=${attempt}`);
            return true;
          }
          log(`final-put-EC-${r.EC}`, `EM=${r.EM || ''} attempt=${attempt}`);
          return false;
        } catch (err) {
          const msg = (err as { message?: string })?.message || 'unknown';
          log(`final-put-FAIL`, `attempt=${attempt} msg="${msg}"`);
          if (attempt >= 3) return false;
          // Exponential backoff: 1s, 3s before giving up after 3 tries.
          await new Promise(res => setTimeout(res, attempt * 2000 + 1000));
          return putWithRetry(attempt + 1);
        }
      };
      const ok = await putWithRetry();
      if (!ok) {
        // All retries exhausted — surface as error so operator can
        // click retry. Server likely has the data (we saw 200 in
        // upstream logs even when client reported network error), so
        // operator re-opening the record usually shows the data intact.
        throw new Error('Mất kết nối khi lưu — thử "làm mới" để kiểm tra.');
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      log('ERROR', e?.message || 'unknown');
      try {
        await updatePendingRecordApi(recordId, {
          status: 'error',
          errorMsg: e?.message || 'Lỗi xử lý',
          currentStep: null,
        });
      } catch { /* ignore */ }
      setQueueRecords(prev => prev.map(r => r.id === recordId
        ? { ...r, status: 'error', errorMsg: e?.message || 'Lỗi xử lý', currentStep: null } : r));
    }
  }, []);

  // Max records detected in parallel. 2 keeps the Mistral free-tier
  // RPM (~10) safe when each record makes ~1-2 OCR calls, and matches
  // the browser's practical upload-over-tunnel bandwidth. Bump this
  // only after confirming Mistral RPM headroom.
  const MAX_CONCURRENT = 2;
  const inFlightRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    // Schedule as many pending rows as free slots allow — no longer
    // one-at-a-time. `inFlightRef` tracks active record IDs so state
    // churn (setQueueRecords mid-detection) doesn't re-trigger the
    // same record. The old single-slot lock meant a batch of 10 CCCDs
    // took ~50s serially; 2-way concurrency roughly halves that for
    // the common case.
    const slots = MAX_CONCURRENT - inFlightRef.current.size;
    if (slots <= 0) return;

    const pendingRows = queueRecords.filter(r =>
      r.status === 'pending' && !inFlightRef.current.has(r.id)
    );
    if (pendingRows.length === 0) return;

    const toStart = pendingRows.slice(0, slots);
    // eslint-disable-next-line no-console
    console.log(`[QUEUE:dispatch] slots=${slots} inFlight=[${[...inFlightRef.current].join(',')}] starting=[${toStart.map(r => r.id).join(',')}] pendingTotal=${pendingRows.length}`);
    for (const row of toStart) {
      const files = filesMap.current.get(row.id);
      if (!files || files.length === 0) {
        // Stale record from a previous session — mark error so the
        // operator can clean it up. Don't consume a slot.
        setQueueRecords(prev => prev.map(r => r.id === row.id
          ? { ...r, status: 'error', errorMsg: 'Không có ảnh (reload) — hãy xóa và thêm lại', currentStep: null } : r));
        updatePendingRecordApi(row.id, {
          status: 'error',
          errorMsg: 'Không có ảnh (reload) — hãy xóa và thêm lại',
          currentStep: null,
        }).catch(() => {});
        continue;
      }
      inFlightRef.current.add(row.id);
      setQueueProcessing(true);
      processRecord(row.id, files).finally(() => {
        inFlightRef.current.delete(row.id);
        if (inFlightRef.current.size === 0) setQueueProcessing(false);
      });
    }
  }, [queueRecords, processRecord]);

  const addRecord = useCallback(async (
    input: FileList | File[] | StagingItem[],
    ttCourseId?: number | null,
  ) => {
    // Normalise input → always a StagingItem[]. When the host page hands us
    // raw File[] (legacy path), default the hint to 'cccd' to preserve the
    // previous auto-classify behaviour — the backend treats 'cccd' as "run
    // the short CCCD prompt"; if the image is actually a GPLX or portrait,
    // it'll extract nothing and land in extras, same as before.
    const arr: (File | StagingItem)[] = input instanceof FileList
      ? Array.from(input)
      : (input as (File | StagingItem)[]);
    const items: StagingItem[] = arr.map(x => isStagingItem(x)
      ? x
      : ({ file: x as File, hint: 'cccd' as DocTypeHint }));
    if (items.length === 0) return;
    try {
      const resp = await createPendingRecordApi({
        status: 'pending',
        fileCount: items.length,
        scanMethod: 'ocr',
        // Scope the pending row to the currently-selected course so the
        // "Chờ lưu hồ sơ" tab filter (ttCourseId === currentCourseId)
        // shows it. Without this the row lands with ttCourseId=null and
        // the tab filter hides it.
        ...(ttCourseId != null ? { ttCourseId } : {}),
      });
      if (resp.EC === 0 && resp.DT) {
        filesMap.current.set(resp.DT.id, items);
        setQueueRecords(prev => [...prev, resp.DT!]);
      }
    } catch { /* silent fail — operator can retry */ }
  }, []);

  const removeRecord = useCallback(async (id: number) => {
    try {
      await deletePendingRecordApi(id);
      filesMap.current.delete(id);
      setQueueRecords(prev => prev.filter(r => r.id !== id));
    } catch { /* silent */ }
  }, []);

  const clearQueue = useCallback(async () => {
    try {
      await clearAllPendingRecordsApi();
      filesMap.current.clear();
      setQueueRecords([]);
    } catch { /* silent */ }
  }, []);

  const retryRecord = useCallback(async (id: number) => {
    try {
      await updatePendingRecordApi(id, { status: 'pending', errorMsg: null });
      setQueueRecords(prev => prev.map(r => r.id === id ? { ...r, status: 'pending', errorMsg: null } : r));
    } catch { /* silent */ }
  }, []);

  const replaceRecord = useCallback((updated: PendingRecordDto) => {
    setQueueRecords(prev => prev.map(r => r.id === updated.id ? updated : r));
  }, []);

  /** Remove a record from local state without a DELETE API call — use after
   *  the backend already deleted it (e.g. savePendingRecord atomic flow). */
  const deleteLocal = useCallback((id: number) => {
    filesMap.current.delete(id);
    setQueueRecords(prev => prev.filter(r => r.id !== id));
  }, []);

  const doneCount = queueRecords.filter(r => r.status === 'done').length;
  const errorCount = queueRecords.filter(r => r.status === 'error').length;
  const pendingCount = queueRecords.filter(r => r.status === 'pending' || r.status === 'detecting').length;

  // Re-fetch from server — call after bulk transfer / course close so
  // local ttCourseId values stay in sync with the DB.
  const refreshQueue = useCallback(async () => {
    try {
      const resp = await listPendingRecordsApi();
      if (resp.EC === 0 && Array.isArray(resp.DT)) {
        setQueueRecords(resp.DT);
      }
    } catch { /* silent */ }
  }, []);

  return {
    queueRecords,
    queueProcessing,
    doneCount,
    errorCount,
    pendingCount,
    addRecord,
    removeRecord,
    clearQueue,
    retryRecord,
    replaceRecord,
    deleteLocal,
    refreshQueue,
  };
};
